import React, { useEffect, useState } from 'react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import {
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Table,
    Paper,
    CircularProgress,
    Chip,
    TablePagination,
} from '@mui/material';
import { Download, Search } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CustomButton from '../../componentsII/Buttons';

const CronLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalLogs, setTotalLogs] = useState(0);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let query = `page=${page + 1}&limit=${rowsPerPage}`;
            if (startDate) query += `&start=${startDate.toISOString()}`;
            if (endDate) query += `&end=${endDate.toISOString()}`;

            const res = await api.get(`/cron/cron-logs?${query}`);
            setLogs(res.data.logs || []);
            setTotalLogs(res.data.total || 0);
        } catch (err) {
            console.error('Failed to fetch logs', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const downloadLogs = async () => {
        try {
            let query = '';
            if (startDate) query += `start=${startDate.toISOString()}`;
            if (endDate) query += `${query ? '&' : ''}end=${endDate.toISOString()}`;

            // Use the same API instance that has your auth headers configured
            const response = await api.get(`/cron/cron-logs/download${query ? '?' + query : ''}`, {
                responseType: 'blob' // Important for handling binary data
            });

            // Create a download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            // Name the file based on your date range
            const filename = `cron-logs-${startDate ? startDate.toISOString().split('T')[0] : 'from-start'}_to_${endDate ? endDate.toISOString().split('T')[0] : 'now'}.txt`;
            link.setAttribute('download', filename);

            // Append, click and remove
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Failed to download logs', err);
        }
    };

    const getChipColor = (level) => {
        if (level === 'INFO') return 'primary';
        if (level === 'WARN') return 'warning';
        if (level === 'ERROR') return 'error';
        return 'default';
    };

    useEffect(() => {
        fetchLogs();
    }, [page, rowsPerPage]);

    const parseLog = (raw) => {
        const regex = /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[(\w+)\]:\s?(.*)$/;
        const match = raw?.trim()?.match(regex);
        return {
            timestamp: match?.[1] || '-',
            level: match?.[2] || 'INFO',
            message: match?.[3] || raw,
        };
    };

    return (
        <>
            <PageHeader title="Cron Logs">
                <div className="flex justify-between items-center gap-5 mt-5 flex-wrap">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                dateFormat="yyyy-MM-dd"
                                className="border rounded px-3 py-1 text-lg"
                                placeholderText="Start Date"
                            />
                        </div>
                        <div>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                dateFormat="yyyy-MM-dd"
                                className="border rounded px-3 py-1 text-lg"
                                placeholderText="End Date"
                            />
                        </div>
                        <CustomButton className="bg-green-600 hover:bg-green-700 text-white" click={() => fetchLogs()} text={<><Search size={18} /><span>Filter</span></>} />
                        <CustomButton className="bg-green-600 hover:bg-green-700 text-white" click={downloadLogs} text={<><Download size={18} /><span>Download</span></>} />
                    </div>
                </div>
            </PageHeader>

            <div className="mt-8">
                {loading ? (
                    <div className="flex justify-center items-center h-48">
                        <CircularProgress />
                    </div>
                ) : (
                    <>
                        <TableContainer component={Paper}>
                            <Table >
                                <TableHead className="bg-gray-100">
                                    <TableRow>
                                        <TableCell >Timestamp</TableCell>
                                        <TableCell >Level</TableCell>
                                        <TableCell>Message</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {logs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No logs found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        logs.map((log, idx) => {
                                            const parsed = parseLog(log.raw);
                                            return (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        {new Date(parsed.timestamp).toLocaleString()}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={parsed.level}
                                                            color={getChipColor(parsed.level)}
                                                            size="small"
                                                            className="uppercase font-medium"
                                                        />
                                                    </TableCell>
                                                    <TableCell>{parsed.message}</TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={totalLogs}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className="px-4"
                            labelDisplayedRows={({ from, to, count }) =>
                                `Page ${page + 1} of ${Math.ceil(count / rowsPerPage) || 1} | ${from}-${to} of ${count}`
                            }
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default CronLogs;
